import type { Cart, CartProgram, LanguageIsoCode, Program } from '@/graphql/graphql';
import { addToCart as apiAddToCart, getCurrentCart } from '@/lib/api-cart';
import { useCallback, useEffect, useState } from 'react';
import { CartContext } from './cart-context';
import { useUserData } from './user-data-context';

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [cart, setCart] = useState<Cart | null>(null);
    const { isBuyer } = useUserData();

    useEffect(() => {
        if (isBuyer) {
            (async () => setCart(await getCurrentCart()))();
        }
    }, [isBuyer]);

    const refreshCart = useCallback(async () => {
        const updatedCart = await getCurrentCart();
        setCart(updatedCart);
        return updatedCart;
    }, []);

    const addToCart = async (program: Program, lang: LanguageIsoCode) => {
        const currentCart = await refreshCart();

        if (currentCart) {
            await apiAddToCart(currentCart, program, lang);
            refreshCart();
        }
    };

    const canAddToCart = async (programId: string, programLang: string) => {
        const currentCart = await refreshCart();

        if (currentCart) {
            return !currentCart.programs.find(
                (p: CartProgram) => p.program.identifier.id === programId && p.language === programLang,
            );
        }

        return false;
    };

    return (
        <CartContext.Provider value={{ cart, refreshCart, addToCart, canAddToCart }}>{children}</CartContext.Provider>
    );
};
